import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";

interface IvrVersion {
  id: number;
  teamId: number;
  workflowId?: number;
  ivrVoiceConfigurationId?: number;
  fallbackTransferNumber?: string;
  changedBy: string;
  changeType: string;
  savedAt: string; // "2026-03-08 09:00:00 UTC"
}

interface Props {
  integrationId: number;
  onRestore(version: IvrVersion): void;
  onClose(): void;
}

function formatSavedAt(savedAt: string): { primary: string; secondary: string } {
  const date = new Date(savedAt.replace(" UTC", "Z"));
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yestStart = new Date(todayStart);
  yestStart.setDate(yestStart.getDate() - 1);
  const versionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (versionDay.getTime() === todayStart.getTime())
    return { primary: "Today", secondary: timeStr };
  if (versionDay.getTime() === yestStart.getTime())
    return { primary: "Yesterday", secondary: timeStr };
  return {
    primary: date.toLocaleDateString([], { month: "short", day: "numeric" }),
    secondary: timeStr
  };
}

function shortEmail(email: string): string {
  if (!email) return "Unknown";
  return email.split("@")[0];
}

function ChangeTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    Updated: "bg-blue-50 text-blue-700 border border-blue-200",
    Created: "bg-green-50 text-green-700 border border-green-200",
    PreRestore: "bg-slate-100 text-slate-500 border border-slate-200"
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[type] ?? map.Updated}`}>
      {type}
    </span>
  );
}

export default function IvrVersionHistoryPanel({ integrationId, onRestore, onClose }: Props) {
  const [versions, setVersions] = useState<IvrVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const api = useApi();
  const { getTeamNameById } = useTeams();

  useEffect(() => {
    loadVersions();
  }, [integrationId]);

  async function loadVersions() {
    setLoading(true);
    setError("");
    try {
      const response = await api.messaging.get(`Sms/Integrations/${integrationId}/Versions`);
      const data = await response.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load version history.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore(version: IvrVersion) {
    setRestoringId(version.id);
    try {
      const response = await api.messaging.post(
        `Sms/Integrations/${integrationId}/Restore`,
        { versionId: version.id }
      );
      if (response.ok) {
        onRestore(version);
      } else {
        setError("Failed to restore version. Please try again.");
      }
    } catch {
      setError("Failed to restore version.");
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-white border-l border-slate-200 shadow-xl z-30 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Version History</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Restoring loads the config into the form — save to apply it.
          </p>
        </div>
        <button
          aria-label="Close version history"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="p-6 text-sm text-slate-500 text-center">Loading versions…</div>
        )}
        {!loading && error && (
          <div className="p-4 text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && versions.length === 0 && (
          <div className="p-6 text-sm text-slate-500 text-center">
            No version history yet.
            <br />
            <span className="text-xs text-slate-400 mt-1 block">
              A version is saved every time you save changes to this number.
            </span>
          </div>
        )}
        {!loading && !error && versions.map((v, idx) => {
          const { primary, secondary } = formatSavedAt(v.savedAt);
          const isLatest = idx === 0;
          const isRestoring = restoringId === v.id;
          const teamName = getTeamNameById(v.teamId) ?? `Team ${v.teamId}`;

          return (
            <div
              key={v.id}
              className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Timeline dot */}
                <div className="flex flex-col items-center pt-1 flex-shrink-0">
                  <div className={`w-2.5 h-2.5 rounded-full ${isLatest ? "bg-cornflower-blue-500" : "bg-slate-300"}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-slate-800">{primary}</span>
                    <span className="text-sm text-slate-400">{secondary}</span>
                    <ChangeTypeBadge type={v.changeType ?? "Updated"} />
                    {isLatest && (
                      <span className="text-xs font-medium bg-cornflower-blue-50 text-cornflower-blue-700 border border-cornflower-blue-200 px-2 py-0.5 rounded-full">
                        Latest
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 space-y-0.5">
                    <div>by {shortEmail(v.changedBy ?? "")}</div>
                    <div>Team: {teamName}</div>
                    {v.workflowId ? (
                      <div>Workflow #{v.workflowId}</div>
                    ) : (
                      <div className="text-slate-300">No workflow assigned</div>
                    )}
                    {v.fallbackTransferNumber && (
                      <div>Fallback: {v.fallbackTransferNumber}</div>
                    )}
                  </div>
                </div>

                {/* Restore button */}
                <button
                  onClick={() => handleRestore(v)}
                  disabled={!!restoringId}
                  className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 ${
                    isRestoring
                      ? "bg-slate-100 text-slate-400 border-slate-200 cursor-wait"
                      : "bg-white text-cornflower-blue-600 border-cornflower-blue-200 hover:bg-cornflower-blue-50 hover:border-cornflower-blue-300"
                  }`}
                  aria-label={`Restore version from ${primary} at ${secondary}`}
                >
                  {isRestoring ? "Loading…" : (
                    <>
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                        <path d="M3 3v5h5" />
                      </svg>
                      Restore
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
